import React, { useState } from "react";

// THE SCHEMA-COMPLIANT JSON
const SCENE = {
  meta: {
    id: "repair-attempt-timing",
    title: "The Repair Attempt Loop",
    topic: "Timing & Trust",
  },
  cast: {
    speaker: { name: "Spouse", role: "Speaker" },
    listener: { name: "Father", role: "Listener" } // You
  },
  context: {
    summary: "Spouse came home on time (repair attempt). Father ran late with kids (missed update).",
    timeline: [
      "6:00pm: Spouse leaves, promises 9:30pm return.",
      "9:30pm: Spouse returns on time (expecting connection).",
      "10:30pm: Father returns late w/ kids (no update text)."
    ]
  },
  steps: {
    step0_contact: {
      speakerSays: "I came home at 9:30 like we agreed. I’m trying here — I don’t want this to become another fight.",
      listenerHears: "⚠ JUDGMENT / CONTROL DETECTED"
    },
    step1_internalDiagnostics: {
      listenerStory: "Here we go again. She's ignoring that I did something great with the kids.",
      listenerSensation: "Tight chest, defensive energy.",
      listenerUrge: "Explain why she's wrong / Bring up past scorecard."
    },
    step2_externalForensics: {
      objectiveFacts: "Spouse returned on time. You were late. No update text sent.",
      wrapper: "Defensive / Sharp Tone",
      coreNeed: "Spouse needs their effort to rebuild trust to be seen as effort."
    },
    step3_rewrite: {
      defaultReaction: "You’re never realistic about timing. I did my thing with the kids — how are you not just happy?",
      wiseResponse: "I see that you came home when you said you would — thank you. We ran late and I should’ve texted. Let's salvage some time together now."
    },
    step4_resolution: {
      outcome: "Repair acknowledged. Logistics addressed without judgment."
    },
    step5_upgrade: {
      patternName: "The 'Effort = Danger' Loop",
      coreTruth: "When a repair attempt is met with critique, the brain records it as a punishment. You stop trying not because you don't care, but to protect yourself from the pain of rejection.",
      
      // NEW PSYCHOLOGY SECTION
      psychology: {
        title: "Why effort feels dangerous",
        insight: "Vulnerability requires lowering your shield. If you lower your shield (come home on time) and get stabbed (critiqued for a text), your nervous system learns: 'Trying is unsafe.' It withdraws to prevent future pain."
      },

      coachSummary: [
        "Effort must be recognized before logistics are corrected.",
        "Repair attempts are fragile — mishandling them causes withdrawal."
      ],
      oneOnOne: {
        speaker: {
          hardTruth: "If your repair attempts get judged instead of recognized, you will eventually stop offering them.",
          cleanSignal: "Name the effort: 'I came home on time because I’m trying.'"
        },
        listener: {
          hardTruth: "If you correct logistics before recognizing effort, you unintentionally punish the repair.",
          newRule: "Repair attempts get acknowledged before any critique."
        }
      },
      togetherPlan: {
        agreement: "If plans change by >20 mins, send one update text.",
        repairScript: "Speaker: 'I tried to do what we agreed.' // Listener: 'I see that — thank you.'"
      },
      experiment7Day: {
        successMetric: "Fewer repeat fights. Faster recovery."
      }
    }
  }
};

export default function RepairAttemptCard() {
  const [step, setStep] = useState(0);
  const [activeTab, setActiveTab] = useState("summary");

  const nextStep = () => setStep((s) => s + 1);
  const reset = () => {
    setStep(0);
    setActiveTab("summary");
  };

  // STYLE CONFIG
  const isDark = true;
  const cardBg = "#1E293B";
  const textColor = "#F1F5F9";
  const mutedColor = "#94A3B8";
  const borderColor = "#475569";
  const accentColor = "#10B981"; // Emerald Green for this card (Growth/Repair)

  return (
    <section
      className="card lg section"
      style={{
        position: "relative",
        background: cardBg,
        color: textColor,
        border: `1px solid ${borderColor}`,
        overflow: "hidden",
        padding: 0,
        marginTop: 24, // Spacing from the previous card
        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5)"
      }}
    >
      {/* HEADER */}
      <div style={{ background: "rgba(0,0,0,0.3)", padding: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${borderColor}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: step === 5 ? accentColor : "#F59E0B" }} />
          <span className="xs" style={{ fontFamily: "monospace", letterSpacing: "0.1em", color: mutedColor, fontSize: 11 }}>
             SCENE 02 // {SCENE.meta.title.toUpperCase()}
          </span>
        </div>
        {step > 0 && <button onClick={reset} className="xs" style={{ background: "transparent", border: `1px solid ${borderColor}`, color: mutedColor, padding: "2px 8px", borderRadius: 4, cursor: "pointer" }}>RESET</button>}
      </div>

      {/* CAST CONTEXT */}
      <div style={{ background: "#0F172A", padding: "8px 20px", borderBottom: `1px solid ${borderColor}`, display: 'flex', gap: 24 }}>
        <div className="xs" style={{ color: mutedColor }}>SPEAKER: <span style={{ color: "#FCA5A5", fontWeight: 700 }}>{SCENE.cast.speaker.name}</span></div>
        <div className="xs" style={{ color: mutedColor }}>LISTENER: <span style={{ color: "#93C5FD", fontWeight: 700 }}>{SCENE.cast.listener.name} (You)</span></div>
      </div>

      {/* STAGE */}
      <div style={{ padding: "32px 24px", minHeight: 460, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        
        {/* STEP 0 */}
        {step === 0 && (
          <div className="animate-in" style={{ textAlign: "center" }}>
            <p className="xs" style={{color: mutedColor, marginBottom: 16}}>CONTEXT: {SCENE.context.summary}</p>
            <div style={{ marginBottom: 32, textAlign: 'left', background: '#334155', borderRadius: '12px 12px 12px 0', padding: 20, maxWidth: '90%', margin: '0 auto 32px' }}>
               <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 8 }}>{SCENE.cast.speaker.name} SAYS:</div>
               <div style={{ fontFamily: 'serif', fontSize: '1.25rem', fontStyle: 'italic', color: '#fff' }}>"{SCENE.steps.step0_contact.speakerSays}"</div>
            </div>
            <div style={{ borderTop: `1px dashed ${borderColor}`, paddingTop: 24 }}>
               <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 8 }}>YOU HEAR:</div>
               <div className="xs" style={{ color: "#EF4444", fontWeight: 700, background: 'rgba(239, 68, 68, 0.1)', display: 'inline-block', padding: '6px 12px', borderRadius: 4, border: '1px solid #EF4444' }}>{SCENE.steps.step0_contact.listenerHears}</div>
            </div>
            <button onClick={nextStep} style={{ marginTop: 32, background: "#EF4444", color: "white", border: "none", padding: "14px 28px", fontWeight: 700, borderRadius: 50, cursor: "pointer" }}>⏸ PAUSE REACTION</button>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 8px" }}>Internal Diagnostics</h3>
             <div style={{ background: "rgba(0,0,0,0.3)", borderRadius: 8, padding: 16, border: "1px dashed #F59E0B", marginBottom: 24 }}>
              <div className="xs" style={{ color: "#F59E0B", fontWeight: 700, marginBottom: 12 }}>SYSTEM_LOGS</div>
              <ul className="xs" style={{ color: "#FCA5A5", fontFamily: 'monospace', listStyle: 'none', padding: 0, margin: 0 }}>
                <li style={{marginBottom: 6}}>{`> STORY: "${SCENE.steps.step1_internalDiagnostics.listenerStory}"`}</li>
                <li style={{marginBottom: 6}}>{`> SENSATION: ${SCENE.steps.step1_internalDiagnostics.listenerSensation}`}</li>
                <li style={{color: '#EF4444'}}>{`> URGE: ${SCENE.steps.step1_internalDiagnostics.listenerUrge}`}</li>
              </ul>
            </div>
            <div style={{ textAlign: "center" }}>
              <button onClick={nextStep} className="pill" style={{ background: "#F59E0B", color: "#451A03", borderColor: "#F59E0B", fontWeight: 600 }}>Stabilize Systems →</button>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 24px" }}>Forensics</h3>
            <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid #3B82F6", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <div className="xs" style={{ color: "#93C5FD", fontWeight: 700 }}>FACTS ONLY:</div>
              <div className="sm" style={{ color: "#BFDBFE" }}>{SCENE.steps.step2_externalForensics.objectiveFacts}</div>
            </div>
             <div className="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.4)", padding: 16, borderRadius: 12 }}>
                <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700 }}>WRAPPER</div>
                <div className="xs" style={{ color: "#FECACA" }}>{SCENE.steps.step2_externalForensics.wrapper}</div>
              </div>
              <div style={{ background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.5)", padding: 16, borderRadius: 12 }}>
                <div className="xs" style={{ color: "#93C5FD", fontWeight: 700 }}>CORE NEED</div>
                <div className="xs" style={{ color: "#BFDBFE" }}>"{SCENE.steps.step2_externalForensics.coreNeed}"</div>
              </div>
            </div>
            <div style={{ textAlign: "center", marginTop: 24 }}>
              <button onClick={nextStep} className="pill" style={{ background: "transparent", color: "#93C5FD", borderColor: "#3B82F6" }}>Identify Response →</button>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: textColor, textAlign: 'center', margin: "0 0 24px" }}>Script Editor</h3>
            <div style={{ margin: "32px 0", textAlign: "left" }}>
               <div style={{ opacity: 0.4, marginBottom: 16, fontFamily: 'monospace' }} className="sm">
                <span className="xs" style={{color: '#EF4444', display: 'block'}}>DEFAULT REACTION:</span> 
                "{SCENE.steps.step3_rewrite.defaultReaction}"
              </div>
              <div style={{ background: "rgba(34, 197, 94, 0.1)", borderLeft: "4px solid #22C55E", padding: 16, borderRadius: "0 8px 8px 0", color: "#86EFAC", fontFamily: 'monospace', fontSize: '1.0rem' }}>
                <span className="xs" style={{color: '#22C55E', fontWeight: 700, display: 'block'}}>WISE RESPONSE:</span> 
                "{SCENE.steps.step3_rewrite.wiseResponse}"
              </div>
            </div>
            <div style={{ textAlign: "center" }}>
              <button onClick={nextStep} className="pill" style={{ background: "#22C55E", color: "#022C22", borderColor: "#22C55E", fontWeight: 700 }}>Execute →</button>
            </div>
          </div>
        )}

        {/* STEP 4 */}
        {step === 4 && (
           <div className="animate-in" style={{ textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✨</div>
            <h3 className="h3" style={{ color: textColor, margin: "0 0 16px" }}>Resolution</h3>
            <p className="sm" style={{ color: mutedColor, maxWidth: 400, margin: "0 auto 24px" }}>{SCENE.steps.step4_resolution.outcome}</p>
            <button onClick={nextStep} className="pill" style={{ background: "transparent", color: accentColor, borderColor: accentColor }}>View Coach Notes →</button>
          </div>
        )}

        {/* STEP 5: UPGRADE */}
        {step === 5 && (
          <div className="animate-in">
            <h3 className="h3" style={{ color: accentColor, textAlign: 'center', margin: "0 0 4px" }}>Coaching Session</h3>
            <p className="xs" style={{ color: mutedColor, textAlign: 'center', marginBottom: 20 }}>Breaking the pattern.</p>

             <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: `1px solid ${borderColor}`, paddingBottom: 16, overflowX: 'auto' }}>
              {[{ id: "summary", label: "PATTERN" }, { id: "spk", label: "SPEAKER 1:1" }, { id: "lis", label: "LISTENER 1:1" }, { id: "plan", label: "PLAN" }].map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{ background: activeTab === tab.id ? "rgba(255,255,255,0.1)" : "transparent", color: activeTab === tab.id ? "#fff" : mutedColor, border: "none", borderRadius: 4, padding: "6px 12px", fontSize: "0.75rem", fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>{tab.label}</button>
              ))}
            </div>

            <div style={{ minHeight: 180 }}>
              
              {/* TAB 1: SUMMARY (Updated with Psychology) */}
              {activeTab === "summary" && (
                <div className="animate-in">
                  <div style={{ background: `rgba(16, 185, 129, 0.1)`, borderLeft: `4px solid ${accentColor}`, padding: 16, borderRadius: "0 8px 8px 0", marginBottom: 16 }}>
                    <div className="xs" style={{ color: accentColor, fontWeight: 700 }}>CORE TRUTH</div>
                    <p className="sm" style={{ color: "#E2E8F0", fontStyle: "italic", margin: 0 }}>"{SCENE.steps.step5_upgrade.coreTruth}"</p>
                  </div>
                  
                  {/* NEW PSYCHOLOGY BOX */}
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 4 }}>
                      {SCENE.steps.step5_upgrade.psychology.title.toUpperCase()}
                    </div>
                    <div className="sm" style={{ color: "#E2E8F0" }}>
                      {SCENE.steps.step5_upgrade.psychology.insight}
                    </div>
                  </div>
                </div>
              )}
               
              {activeTab === "spk" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#FCA5A5", fontWeight: 700, marginBottom: 12 }}>COACH TO SPOUSE:</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 16 }}>"{SCENE.steps.step5_upgrade.oneOnOne.speaker.hardTruth}"</p>
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor }}>CLEAN SIGNAL:</div>
                    <div className="sm" style={{ color: "#fff" }}>"{SCENE.steps.step5_upgrade.oneOnOne.speaker.cleanSignal}"</div>
                  </div>
                </div>
              )}
               {activeTab === "lis" && (
                <div className="animate-in">
                  <div className="xs" style={{ color: "#93C5FD", fontWeight: 700, marginBottom: 12 }}>COACH TO YOU:</div>
                  <p className="sm" style={{ color: "#E2E8F0", marginBottom: 16 }}>"{SCENE.steps.step5_upgrade.oneOnOne.listener.hardTruth}"</p>
                  <div style={{ background: "#334155", padding: 16, borderRadius: 8 }}>
                    <div className="xs" style={{ color: mutedColor }}>NEW RULE:</div>
                    <div className="sm" style={{ color: "#fff" }}>"{SCENE.steps.step5_upgrade.oneOnOne.listener.newRule}"</div>
                  </div>
                </div>
              )}
              {activeTab === "plan" && (
                <div className="animate-in">
                   <div className="xs" style={{ color: accentColor, fontWeight: 700, marginBottom: 12 }}>7-DAY EXPERIMENT</div>
                   <ul className="sm" style={{ color: "#E2E8F0", paddingLeft: 20 }}>
                    <li style={{marginBottom: 8}}><strong>Agreement:</strong> {SCENE.steps.step5_upgrade.togetherPlan.agreement}</li>
                    <li style={{marginBottom: 8}}><strong>Script:</strong> {SCENE.steps.step5_upgrade.togetherPlan.repairScript}</li>
                    <li><strong>Success:</strong> {SCENE.steps.step5_upgrade.experiment7Day.successMetric}</li>
                   </ul>
                   <button onClick={reset} style={{ width: "100%", background: accentColor, color: "#064E3B", border: "none", padding: "12px", borderRadius: 8, fontWeight: 700, marginTop: 16, cursor: "pointer" }}>Commit & Reset</button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
       <div style={{ height: 4, background: "#334155", width: "100%" }}>
        <div style={{ height: "100%", background: step === 5 ? accentColor : "#F59E0B", width: `${(step / 5) * 100}%`, transition: "width 0.3s ease" }} />
      </div>
    </section>
  );
}
